// src/pages/basket/components/ProductSearchTable.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { useAddOrUpdateBasketMutation } from "../../../../api/endpoints/basket";
import { Tag } from "primereact/tag";

const ProductSearchTable = ({
  data,
  totalRecords,
  isLoading,
  onPage,
  lazyParams,
  resetPagination = false,
  refetchBasket
}) => {
  const { th } = useLanguage();
  const toast = useRef(null);
  const [addOrUpdateBasket, { isLoading: isAdding }] = useAddOrUpdateBasketMutation();

  const [addingCode, setAddingCode] = useState(null);

  const [quantities, setQuantities] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [currentFirst, setCurrentFirst] = useState(0);

  const safeParams = lazyParams || { page: 1, rows: 10 };

  useEffect(() => {
    if (resetPagination) {
      setCurrentFirst(0);
      setSortField(null);
      setSortOrder(null);
    }
  }, [resetPagination]);

  const handlePage = (event) => {
    setCurrentFirst(event.first);
    onPage?.(event);
  };

  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const stockStatusTag = (status) => (
  <Tag
    value={status ? (th("product.stockAvailable") || "Available")
                  : (th("product.stockUnavailable") || "Not Available")}
    severity={status ? "success" : "danger"}
  />
);

  const sortedData = useMemo(() => {
    if (!sortField || !sortOrder || !data) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let comparison = 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        comparison = aVal.localeCompare(bVal, "tr", { numeric: true });
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else {
        comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return sortOrder * comparison;
    });
  }, [data, sortField, sortOrder]);

  const handleAddToBasket = async (row) => {
    if (isAdding) return;

    const partCode = row.partCode;
    const quantity = quantities[partCode] || 1;

    // Boş veya 0 değer kontrolü
    if (!quantity || quantity < 1) {
      toast.current.show({
        severity: "warn",
        summary: th("common.warning"),
        detail: th("product.minQuantityRequired"),
        life: 4000,
      });
      return;
    }

    const limit = Number(row.internalOrderLimit);
    if (!Number.isNaN(limit) && limit > 0) {
      // Minimum değer kontrolü
      if (quantity < limit) {
        toast.current.show({
          severity: "warn",
          summary: th("common.warning"),
          detail: th("product.minOrderLimitNotMet") !== "product.minOrderLimitNotMet"
            ? `${th("product.minOrderLimitNotMet")}: ${limit}`
            : `Minimum sipariş adedi: ${limit}`,
          life: 4000,
        });
        return;
      }
      // Katlar kontrolü (5 ise sadece 5, 10, 15, ... olmalı)
      if (quantity % limit !== 0) {
        toast.current.show({
          severity: "warn",
          summary: th("common.warning"),
          detail: th("product.orderLimitMultiple") !== "product.orderLimitMultiple"
            ? `${th("product.orderLimitMultiple")}: ${limit}`
            : `Sipariş adedi ${limit}'in katları olmalıdır (${limit}, ${limit * 2}, ${limit * 3}, ...)`,
          life: 4000,
        });
        return;
      }
    }

    const payload = {
      itemCode: partCode,
      quantity,
      erpItemId: 0,
      description: "",
    };

    try {
      setAddingCode(partCode);
      const result = await addOrUpdateBasket(payload).unwrap();
      if (result?.isSuccessful) {
        toast.current.show({
          severity: "success",
          summary: th("common.success"),
          detail: th("product.addedToBasket"),
          life: 3000,
        });
        refetchBasket?.();
      }
      // isSuccessful false ise backend'den gelen hata mesajı baseQueryWithReauth tarafından gösteriliyor
    } catch (err) {
      // Hata mesajı baseQueryWithReauth tarafından global olarak gösteriliyor
      console.error(err);
    }
  };

  const textBody = (v) => (v == null || v === "" ? "-" : v);
  const numBody = (v) => (v == null ? "-" : v);

  return (
    <div>
      <Toast ref={toast} />

      <DataTable
        value={sortedData}
        paginator
        lazy
        first={currentFirst}
        rows={safeParams.rows}
        totalRecords={totalRecords}
        loading={isLoading}
        onPage={handlePage}
        onSort={onSort}
        sortField={sortField}
        sortOrder={sortOrder}
        rowsPerPageOptions={[10, 20, 50]}
        dataKey="partCode"
        showGridlines
        stripedRows
        emptyMessage={th("product.empty")}
        removableSort
        sortMode="single"
        responsiveLayout="scroll"
        scrollable
      >
        <Column field="partCode" header={th("product.code")} sortable body={(r) => textBody(r.partCode)} />
        <Column field="partName" header={th("product.name")} sortable body={(r) => textBody(r.partName)} />
        <Column field="brand" header={th("product.brand")} sortable body={(r) => textBody(r.brand)} />
        <Column field="meins" header={th("product.unit")} sortable body={(r) => textBody(r.meins)} />
        <Column field="bursaStockStatus" header={th("product.bursaStock") || "Bursa Stock"} sortable sortField="bursaStockStatus" body={(row) => stockStatusTag(row.bursaStockStatus)}/>
        {data && data.length > 0 && data[0].isShowGermanyStockStatus && (
          <Column field="germanyStockStatus" header={th("product.germanyStock") || "Germany Stock"} sortable sortField="germanyStockStatus" body={(row) => stockStatusTag(row.germanyStockStatus)}/>
        )}
        <Column field="internalOrderLimit" header={th("product.OrderLimit")} sortable body={(r) => numBody(r.internalOrderLimit)} />
        <Column field="retailPrice" header={th("product.retailPrice")} sortable body={(r) => textBody(r.retailPrice)} />
        <Column field="discountAmount" header={th("product.discountAmount")} sortable body={(r) => textBody(r.discountAmount)} />
        <Column field="discountedPrice" header={th("product.discountedPrice")} sortable body={(r) => textBody(r.discountedPrice)} />
        <Column field="deliveryDays" header={th("product.deliveryDay")} sortable body={(r) => numBody(r.deliveryDays)} />
        <Column header={th("product.quantity")} body={(row) => (
          <InputNumber
            value={quantities[row.partCode]}
            onValueChange={(e) => {
              setQuantities((prev) => ({ ...prev, [row.partCode]: e.value }));
            }}
            onFocus={(e) => {
              // Input içeriğini temizle
              setQuantities((prev) => ({ ...prev, [row.partCode]: null }));
            }}
            placeholder="1"
            showButtons
            min={0}
            max={999}
            allowEmpty={true}
            disabled={isAdding}
            inputStyle={{ width: "60px", height: "30px", textAlign: "center" }}
            pt={{
              incrementButton: { style: { height: "15px", padding: 0 } },
              decrementButton: { style: { height: "15px", padding: 0 } },
            }}
          />
        )} />
        <Column header={th("product.addToBasket")} body={(row) => (
          <Button
            icon="pi pi-shopping-cart"
            label={th("product.addToBasket")}
            onClick={() => handleAddToBasket(row)}
            loading={isAdding && addingCode === row.partCode}
            disabled={isAdding}
          />
        )} />
      </DataTable>
    </div>
  );
};

export default ProductSearchTable;
